import { dialCodeForCountry } from "./countries";

/**
 * A patient's WhatsApp number, as the therapist types it and as it is stored.
 *
 * Stored in international E.164 form (`+34600123456`), because every WhatsApp
 * message goes to it and a merely plausible number is a message delivered to
 * a stranger. A national number is completed with the patient's country code
 * in the form, where the therapist sees the result before saving it.
 */

/** E.164 without its `+`: a country code that cannot start with 0, then digits. */
const DIALLABLE = /^[1-9]\d{6,14}$/;

/**
 * The number as `wa.me` wants it — digits only, country code first — or null
 * when it cannot be dialled.
 */
export function whatsappDigits(
    phone: string | null | undefined,
    countryCode: string | null | undefined
): string | null {
    const raw = (phone ?? "").trim();
    if (!raw) return null;

    const digits = raw.replace(/\D/g, "");
    let international: string;
    if (raw.startsWith("+")) {
        international = digits;
    } else if (digits.startsWith("00")) {
        international = digits.slice(2);
    } else {
        const dialCode = dialCodeForCountry(countryCode ?? undefined);
        if (!dialCode) return null;
        // The trunk zero some countries dial nationally is dropped once the
        // country code stands in front of it (07700… in the UK → 447700…).
        international = `${dialCode.slice(1)}${digits.replace(/^0/, "")}`;
    }
    return DIALLABLE.test(international) ? international : null;
}

/** The number in E.164 form (`+34600123456`), or null when it cannot be dialled. */
export function toWhatsAppNumber(
    phone: string | null | undefined,
    countryCode: string | null | undefined
): string | null {
    const digits = whatsappDigits(phone, countryCode);
    return digits ? `+${digits}` : null;
}

/**
 * What a form sends for the WhatsApp number: null for an empty field, the
 * E.164 form otherwise. Undefined when what was typed cannot be dialled — the
 * form reports it rather than sending a number nobody can be reached on.
 */
export function whatsappNumberForForm(
    phone: string | null | undefined,
    countryCode: string | null | undefined
): string | null | undefined {
    if (!(phone ?? "").trim()) return null;
    return toWhatsAppNumber(phone, countryCode) ?? undefined;
}

/** i18n key of the problem with an optional WhatsApp number, or undefined. */
export function validateWhatsAppNumber(
    phone: string | null | undefined,
    countryCode: string | null | undefined
): string | undefined {
    return whatsappNumberForForm(phone, countryCode) === undefined
        ? "patient.whatsappInvalid"
        : undefined;
}
